import { PageHero } from "@/components/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, SectionHeading } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { usePageMeta } from "@/lib/seo";

export const HowItWorks = () => {
    const { t, tHtml } = useLang();
    const CHAPTERS = [
        {
            number: "1",
            title: t("how_it_works.ch1.title", "Share your case"),
            desc: t("how_it_works.ch1.desc", "Tell us about your condition and share relevant medical reports."),
            you: [
                t("how_it_works.ch1.you1", "Complete the medical opinion form"),
                t("how_it_works.ch1.you2", "Describe your condition and history"),
                t("how_it_works.ch1.you3", "Share reports securely after your enquiry"),
            ],
            we: [
                t("how_it_works.ch1.we1", "Review your case with care"),
                t("how_it_works.ch1.we2", "Identify the appropriate specialties"),
                t("how_it_works.ch1.we3", "Coordinate the right specialists"),
            ],
        },
        {
            number: "2",
            title: t("how_it_works.ch2.title", "Meet the right specialist"),
            desc: t("how_it_works.ch2.desc", "We help coordinate an appropriate specialist consultation."),
            you: [
                t("how_it_works.ch2.you1", "Attend the online consultation"),
                t("how_it_works.ch2.you2", "Ask every question you have"),
                t("how_it_works.ch2.you3", "Tell us what matters to you"),
            ],
            we: [
                t("how_it_works.ch2.we1", "Coordinate consultation scheduling"),
                t("how_it_works.ch2.we2", "Help with communication and language"),
                t("how_it_works.ch2.we3", "Follow up on the specialist's guidance"),
            ],
        },
        {
            number: "3",
            title: t("how_it_works.ch3.title", "Plan your treatment"),
            desc: t("how_it_works.ch3.desc", "Understand the recommended treatment pathway and practical requirements."),
            you: [
                t("how_it_works.ch3.you1", "Review the proposed pathway"),
                t("how_it_works.ch3.you2", "Consider timing and travel"),
                t("how_it_works.ch3.you3", "Decide with confidence"),
            ],
            we: [
                t("how_it_works.ch3.we1", "Coordinate information on the pathway"),
                t("how_it_works.ch3.we2", "Help gather cost and requirement details from providers"),
                t("how_it_works.ch3.we3", "Prepare practical next steps with you"),
            ],
        },
        {
            number: "4",
            title: t("how_it_works.ch4.title", "Travel with confidence"),
            desc: t("how_it_works.ch4.desc", "We coordinate the necessary parts of your healthcare journey in India."),
            you: [
                t("how_it_works.ch4.you1", "Travel to Kerala, with family if you wish"),
                t("how_it_works.ch4.you2", "Focus on your treatment and rest"),
                t("how_it_works.ch4.you3", "Keep in touch — we stay available"),
            ],
            we: [
                t("how_it_works.ch4.we1", "Coordinate travel and accommodation with local partners"),
                t("how_it_works.ch4.we2", "Support you during hospital visits"),
                t("how_it_works.ch4.we3", "Stay connected through recovery and follow-up"),
            ],
        },
    ];
    usePageMeta(
        t("meta.how_it_works.title", "How It Works"),
        t("meta.how_it_works.desc", "From sharing your case to travelling home — see how UniversHeal coordinates each stage of your healthcare journey in Kerala, India.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("how_it_works.eyebrow", "How It Works")}
                title={t("how_it_works.title", "Your healthcare journey, made simpler.")}
                intro={t("how_it_works.intro", "Four clear stages. One dedicated coordination team. You always know what happens next.")}
            />

            <section className="section-pad pt-6">
                <div className="container-site max-w-5xl">
                    {CHAPTERS.map((chapter, i) => (
                        <Reveal key={chapter.number} delay={0.05}>
                            <div
                                data-testid={`process-chapter-${i + 1}`}
                                className={`grid md:grid-cols-[1fr_1.6fr] gap-8 py-12 sm:py-16 ${
                                    i > 0 ? "border-t border-pine/15" : ""
                                }`}
                            >
                                <div>
                                    <p className="font-serif italic text-5xl text-natural leading-none">
                                        {chapter.number}
                                    </p>
                                    <h2 className="mt-5 font-serif font-medium text-3xl sm:text-4xl text-forest leading-tight">
                                        {chapter.title}
                                    </h2>
                                    <p className="mt-3 text-sm leading-relaxed text-moss">
                                        {chapter.desc}
                                    </p>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div className="bg-forest text-ivory rounded-2xl p-7">
                                        <p className="eyebrow text-natural">{t("common.you", "You")}</p>
                                        <ul className="mt-5 space-y-3">
                                            {chapter.you.map((item) => (
                                                <li
                                                    key={item}
                                                    className="text-sm leading-relaxed flex items-start gap-2.5"
                                                >
                                                    <span className="mt-1.5 w-1 h-1 rounded-full bg-natural shrink-0" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="bg-white border border-pine/15 rounded-2xl p-7">
                                        <p className="eyebrow text-moss">
                                            {t("common.weCoordinate", "We coordinate")}
                                        </p>
                                        <ul className="mt-5 space-y-3">
                                            {chapter.we.map((item) => (
                                                <li
                                                    key={item}
                                                    className="text-sm leading-relaxed text-charcoal/80 flex items-start gap-2.5"
                                                >
                                                    <span className="mt-1.5 w-1 h-1 rounded-full bg-natural shrink-0" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                    <Reveal>
                        <p className="text-xs leading-relaxed text-moss max-w-2xl mt-4">
                            {t("how_it_works.text1", "Where a service is delivered by trusted local partners, UniversHeal coordinates it on your behalf and remains your single point of contact.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("how_it_works.headline", "Ready when you are.")}
                text={t("how_it_works.text", "Share your case today — it starts with a simple conversation.")}
            />
        </>
    );
};
